import { BlockSpeakChange } from '@visikon/core-models/content';
import useRefState from 'commons/useRefState';
import { useRef, useState, useEffect, SyntheticEvent } from 'react';
import { Button, Dropdown, Icon, Input, Menu, Modal, Popup, Table } from 'semantic-ui-react';
import { ClipboardLinkButton } from '../../../../../components/ClipboardLinkButton';
import Text2SpeechModal from '../../text2speech';
import { FileUploadButton } from '../FileUpload';
import { VariationProps } from '../SharedVarationParts';
import { StatusPicker } from '../StatusPicker';
import { VariationComments } from '../VariationComments';
import { VariationDropDown } from '../VariationDropDown';
import * as Styles from './SpeakVariation.styles';
import { downloadFile } from 'commons/download-file';
import { PreviewReadyToast } from '../../text2speech/toast';
import { LanguageCode } from '@visikon/core-models/languageTypes';
import { VoiceDropdown } from '../../text2speech/VoiceDropdown';
import { findVoiceById } from '../../text2speech/StaticVoiceData';
import PhoneticsTestModal from '../../text2speech/PhoneticsTestModal';
import { usePreferredVoice } from '../../text2speech/UsePreferredVoice';
import { getSpeechRateForVoice, setSpeechRateForVoice } from 'api/v2/text2speechApi';
import { SpeakerVoiceNames } from '../VariationConstants';
import { ArrayToDropdownItems } from 'commons/DropdownUtils';

type Props = VariationProps<BlockSpeakChange, BlockSpeakChange> & { activeLanguage: LanguageCode };

export const SpeakVariation = ({ variation, isActiveVariation, blockVariations, activeLanguage, onChange, onRemove }: Props) => {
  const mediaRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setPlaying] = useState<boolean>(false);
  const [speak, setSpeak, getSpeak] = useRefState<Props['variation']>(variation);
  const [selectedVoiceId, setSelectedVoiceId] = useState('');
  const [selectedVoiceLabel, setSelectedVoiceLabel] = useState('');

  function onEnded() {
    if (mediaRef.current === null) {
      return;
    }

    // @ts-ignore
    delete mediaRef.current.src;
    setPlaying(false);
  }

  const handleFilePicked = (files: File[]) => {
    const file = files[0];
    const mediaTag = mediaRef.current;
    const currentSpeak = getSpeak();
    const previewUrl = URL.createObjectURL(file);

    if (file && mediaTag && currentSpeak) {
      mediaTag.src = previewUrl;
      mediaTag.onloadedmetadata = () => {
        handleVariationChange({
          ...currentSpeak,
          voice: undefined,
          audioFile: {
            contentType: file.type,
            src: previewUrl,
            size: file.size,
            file,
            duration: mediaTag.duration,
          },
        });
      };
    }
  };

  const handleSpeechAttached = (blob: Blob) => {
    const mediaTag = mediaRef.current;
    const currentSpeak = getSpeak();

    const variationName = currentSpeak?.variations?.[0];

    if (blob && mediaTag && currentSpeak && variationName) {
      const voiceIdentifier = selectedVoiceLabel || selectedVoiceId || 'voice';
      const customFileName = `Generated_Audio_[${variationName}]-${activeLanguage}-${voiceIdentifier}.wav`;
      const filename = speak.fileNameForShow || customFileName;
      const file = new File([blob], filename, { type: blob.type });
      const tempUrl = URL.createObjectURL(file);
      const voiceData = findVoiceById(selectedVoiceId);

      mediaTag.src = tempUrl;
      mediaTag.onloadedmetadata = () => {
        handleVariationChange({
          ...currentSpeak,
          voice: voiceData?.variationVoiceName,
          audioFile: {
            contentType: file.type,
            src: tempUrl,
            size: file.size,
            file,
            duration: mediaTag.duration,
          },
        });
      };
    }
  };

  const handlePlayClicked = () => {
    if (mediaRef.current === null) {
      return;
    }

    if (isPlaying) {
      mediaRef.current.pause();
      setPlaying(false);
    } else {
      mediaRef.current.src = speak.audioFile?.src || '';
      mediaRef.current.play();
      setPlaying(true);
    }
  };

  const handleVariationChange = (speakChange: Partial<Props['variation']>) => {
    const newSpeak: Props['variation'] = { ...getSpeak(), ...speakChange };

    setSpeak(newSpeak);
    onChange(newSpeak);
  };

  const handleRemoveSpeak = () => {
    const modifiedSpeak = { ...getSpeak(), voice: undefined };
    onRemove?.(modifiedSpeak.key);
  };

  const handleAutodetectSpeaker = () => {
    const url = getSpeak().fileNameForShow?.toLowerCase();

    if (!url) {
      // eslint-disable-next-line no-console
      console.log('No audio source URL to autodetect speaker from');
      return;
    }

    // Autodetect logic based on voices in SpeakerVoiceNames
    const detectedVoice = SpeakerVoiceNames.find((voice) => url.includes(voice.split(' ')[0].toLowerCase()));

    if (!detectedVoice) {
      // eslint-disable-next-line no-console
      console.log('Could not autodetect speaker from URL', url);
      return;
    }

    handleVariationChange({ voice: detectedVoice });
  };

  const hasSpeak = !!speak.audioFile?.src;

  return (
    <Styles.Container style={{ border: isActiveVariation ? '4px solid #9fcb34' : '' }}>
      <Styles.Content>
        <Table compact basic="very">
          <Table.Body>
            <Table.Row>
              <Table.Cell style={{ width: 54 }}>
                <StatusPicker onChange={(status) => handleVariationChange({ status })} active={speak.status} />
              </Table.Cell>
              <Table.Cell>
                <VariationDropDown
                  defaultValue={speak.variations}
                  blockVariations={blockVariations}
                  onChange={(variations) => handleVariationChange({ variations })}
                />
              </Table.Cell>
            </Table.Row>

            <Table.Row>
              <Table.Cell style={{ width: 54 }}>Speaker</Table.Cell>
              <Table.Cell>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Dropdown
                    openOnFocus
                    placeholder="Select speaker"
                    fluid
                    selection
                    clearable
                    value={speak.voice || ''}
                    onChange={(_, { value }) => handleVariationChange({ voice: value as string })}
                    options={ArrayToDropdownItems(SpeakerVoiceNames)}
                  />
                  <Button onClick={handleAutodetectSpeaker}>Detect</Button>
                </div>
              </Table.Cell>
            </Table.Row>

            <Table.Row style={{ border: 0 }}>
              <Table.Cell colSpan={2}>
                <Menu style={{ alignItems: 'center' }}>
                  <Dropdown item icon="bars" style={{ width: 64, justifyContent: 'center', color: hasSpeak ? 'inherit' : 'red' }}>
                    <Dropdown.Menu>
                      <FileUploadButton text="Upload audio" onFilePick={handleFilePicked} />
                      <DownloadButton speak={speak} />
                      <Text2SpeechButton
                        text={speak.text}
                        onAttached={handleSpeechAttached}
                        hasSpeak={hasSpeak}
                        activeLanguage={activeLanguage}
                        selectedVoiceId={selectedVoiceId}
                        selectedVoiceLabel={selectedVoiceLabel}
                        setVoiceId={setSelectedVoiceId}
                        setVoiceLabel={setSelectedVoiceLabel}
                      />
                      <AdjustText2SpeechRateModal
                        activeLanguage={activeLanguage}
                        selectedVoiceId={selectedVoiceId}
                        setVoiceId={setSelectedVoiceId}
                        setVoiceLabel={setSelectedVoiceLabel}
                      />
                      <Text2SpeechPhoneticsButton activeLanguage={activeLanguage} selectedVoiceId={selectedVoiceId} manuscript={speak.text} />
                      <DeleteButton hasSpeak={hasSpeak} handleRemoveSpeak={handleRemoveSpeak} />
                    </Dropdown.Menu>
                  </Dropdown>
                  <PlayButton hasSpeak={hasSpeak} isPlaying={isPlaying} handlePlayClicked={handlePlayClicked} />
                  <SpeakDuration hasSpeak={hasSpeak} duration={speak.audioFile?.duration} />
                  <PreviewReadyToast toastText="Preview ready!" success={!!speak.audioFile} />
                  <WorkfilesButton fileNameForShow={speak.fileNameForShow} />
                </Menu>
              </Table.Cell>
            </Table.Row>

            <Table.Row>
              <Table.Cell colSpan={2}>
                <Styles.Editor
                  onChange={({ target }) => handleVariationChange({ text: target.value })}
                  defaultValue={speak.text}
                  placeholder="Enter manuscript text..."
                />
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
      </Styles.Content>

      <VariationComments
        title="Comment"
        content={speak.notes}
        sendWrittenNotes={(notes) => handleVariationChange({ notes })}
        placeholder="Write here..."
        shouldExpand={isActiveVariation}
      />

      <video ref={mediaRef} style={{ display: 'none' }} onEnded={onEnded} preload="metadata" crossOrigin="anonymous" />
    </Styles.Container>
  );
};

function PlayButton({ hasSpeak, isPlaying, handlePlayClicked }: { hasSpeak: boolean; isPlaying: boolean; handlePlayClicked: () => void }) {
  return (
    <>
      {hasSpeak && (
        <>
          <Menu.Item name="play" onClick={handlePlayClicked}>
            <Icon name={isPlaying ? 'pause circle' : 'play circle'} />
          </Menu.Item>
        </>
      )}
    </>
  );
}

function SpeakDuration({ hasSpeak, duration }: { hasSpeak: boolean; duration: number | undefined; }) {
  if (!duration || !hasSpeak) return null;

  return (
    <Menu.Item>
      <span>{ Math.floor(duration / 60) }:{ Math.floor(duration % 60).toString().padStart(2, '0') }</span>
    </Menu.Item>
  );
}

function WorkfilesButton({ fileNameForShow }: { fileNameForShow?: string }) {
  return (
    <>
      {fileNameForShow && (
        <Popup
          trigger={
            <Menu.Item position="right">
              <Icon name="file" />
            </Menu.Item>
          }
          content={<ClipboardLinkButton cleanup>{fileNameForShow}</ClipboardLinkButton>}
          header="Original filename"
          on="click"
          size="small"
          position="bottom right"
        />
      )}
    </>
  );
}

function DeleteButton({ hasSpeak, handleRemoveSpeak }: { hasSpeak: boolean; handleRemoveSpeak: () => void }) {
  const featureToggle = { disabled: false };

  return (
    <>
      {!featureToggle.disabled && hasSpeak && (
        <>
          <Dropdown.Divider />
          <Dropdown.Item onClick={handleRemoveSpeak}>
            <Icon name="trash" />
            <span className="text">Remove audio</span>
          </Dropdown.Item>
        </>
      )}
    </>
  );
}

function DownloadButton({ speak }: { speak?: BlockSpeakChange }) {
  const name = speak?.fileNameForShow;
  const src = speak?.audioFile?.src;

  if (!src || !name) return null;

  return (
    <Dropdown.Item onClick={() => downloadFile(src, name)}>
      <Icon name="download" />
      <span className="text">Download audio</span>
    </Dropdown.Item>
  );
}

interface Text2SpeechButtonProps {
  text: string;
  onAttached: (blob: Blob) => void;
  hasSpeak: boolean;
  activeLanguage: LanguageCode;
  selectedVoiceId: string;
  selectedVoiceLabel: string;
  setVoiceId: (voiceId: string) => void;
  setVoiceLabel: (voiceLabel: string) => void;
}

const Text2SpeechButton = ({
  text,
  onAttached,
  hasSpeak,
  activeLanguage,
  selectedVoiceId,
  selectedVoiceLabel,
  setVoiceId,
  setVoiceLabel,
}: Text2SpeechButtonProps) => {
  const handleVoiceChange = (_event: SyntheticEvent<HTMLElement>, data: { value: string }) => {
    if (typeof data.value === 'string') {
      setVoiceId(data.value);
      const selectedVoice = findVoiceById(data.value);
      setVoiceLabel(selectedVoice?.name ?? data.value);
    }
  };

  const { voiceId, voiceLabel } = usePreferredVoice({ selectedLanguage: activeLanguage, selectedVoiceId });

  useEffect(() => {
    if (voiceId) {
      setVoiceId(voiceId);
      setVoiceLabel(voiceLabel);
    }
  }, [voiceId, voiceLabel, setVoiceId, setVoiceLabel]);

  useEffect(() => {
    if (selectedVoiceLabel) {
      setVoiceLabel(selectedVoiceLabel);
    }
  }, [selectedVoiceLabel, setVoiceLabel]);

  const resolvedVoiceId = selectedVoiceId || voiceId;

  if (!resolvedVoiceId) {
    return null;
  }

  return (
    <Text2SpeechModal text={text} onAttached={onAttached} activeLanguage={activeLanguage} selectedVoiceId={resolvedVoiceId}>
      <Dropdown.Item disabled={!!hasSpeak || !text}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Icon name="comment" />
          <span className="text">Generate speech</span>
          <VoiceDropdown activeLanguage={activeLanguage} onChange={handleVoiceChange} />
        </div>
      </Dropdown.Item>
    </Text2SpeechModal>
  );
};

type PhoneticsButtonProps = {
  activeLanguage: LanguageCode;
  selectedVoiceId: string;
  manuscript: string;
};

function Text2SpeechPhoneticsButton({ activeLanguage, selectedVoiceId, manuscript }: PhoneticsButtonProps) {
  if (!selectedVoiceId) return null;

  return (
    <PhoneticsTestModal activeLanguage={activeLanguage || 'da'} selectedVoiceId={selectedVoiceId || ''} manuscript={manuscript}>
      <Dropdown.Item>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Icon name="ald" />
          <span className="text">Test Phonetics</span>
        </div>
      </Dropdown.Item>
    </PhoneticsTestModal>
  );
}

type Text2SpeechRateButtonProps = {
  activeLanguage: LanguageCode;
  selectedVoiceId: string;
  setVoiceId: (voiceId: string) => void;
  setVoiceLabel: (voiceLabel: string) => void;
};

function AdjustText2SpeechRateModal({ activeLanguage, selectedVoiceId, setVoiceId, setVoiceLabel }: Text2SpeechRateButtonProps) {
  const [open, setOpen] = useState(false);
  const [rate, setRate] = useState(0);

  useEffect(() => {
    const speechRate = getSpeechRateForVoice(activeLanguage, selectedVoiceId);
    setRate(speechRate);
  }, [activeLanguage, selectedVoiceId]);

  const saveRate = () => {
    if (selectedVoiceId) {
      setSpeechRateForVoice(activeLanguage, selectedVoiceId, rate);
    }
    setOpen(false);
  };

  const handleVoiceChange = (_event: SyntheticEvent<HTMLElement>, data: { value: string }) => {
    if (typeof data.value === 'string') {
      setVoiceId(data.value);
      const selectedVoice = findVoiceById(data.value);
      setVoiceLabel(selectedVoice?.name ?? data.value);
    }
  };

  if (!selectedVoiceId) {
    return null;
  }

  return (
    <Modal
      centered
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      trigger={
        <Dropdown.Item onClick={() => setOpen(true)}>
          <div style={{ display: 'flex', gap: '0.5em', alignItems: 'center' }}>
            <Icon name="tachometer alternate" />
            <span className="text">Adjust Relative Speech Rate</span>
          </div>
        </Dropdown.Item>
      }
    >
      <Modal.Header>Adjust Relative Speech Rate</Modal.Header>
      <Modal.Content>
        <div style={{ display: 'flex', flexDirection: 'row', gap: 15 }}>
          <VoiceDropdown activeLanguage={activeLanguage} onChange={handleVoiceChange} />
          <Input
            name="speechRate"
            autoComplete="off"
            label="Speech Rate"
            type="number"
            value={rate}
            onChange={(_, data) => setRate(parseInt(`${data.value}`, 10) || 0)}
            placeholder="Enter rate in percentage"
            style={{ width: '100%' }}
          />
        </div>
        <p style={{ marginTop: '1em' }}>
          Adjust the speech rate by entering a value in percentage. For example, enter -10 for a slower rate or 10 for a faster rate.
        </p>
      </Modal.Content>
      <Modal.Actions>
        <Button basic onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <Button color="green" onClick={saveRate}>
          Save
        </Button>
      </Modal.Actions>
    </Modal>
  );
}
